import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { teamService, ROLES } from '../services/teamService';
import { trainingService } from '../services/trainingService';
import { wordService } from '../services/wordService';
import { TopBar } from '../components/common/TopBar';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import './Training.css';


const SETS = [
  { limit: 100, label: 'Top 100' },
  { limit: 200, label: 'Top 200' },
  { limit: 500, label: 'Top 500' },
];

const ROLE_COLORS = {
  speaker1: { bg: 'rgba(255,204,0,0.15)', border: '#FFCC00', text: '#FFCC00', icon: '🗣️', label: 'Speaker 1' },
  speaker2: { bg: 'rgba(255,136,0,0.15)', border: '#FF8800', text: '#FF8800', icon: '🎙️', label: 'Speaker 2' },
  guesser:  { bg: 'rgba(139,92,246,0.15)', border: '#8B5CF6', text: '#8B5CF6', icon: '🎯', label: 'Guesser' },
};

const Training = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Teams
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamDropdownOpen, setTeamDropdownOpen] = useState(false);
  const teamDropdownRef = useRef(null);

  // Real-time team state
  const [liveTeam, setLiveTeam] = useState(null);
  const unsubTeamRef = useRef(null);

  // Personal progress for set display
  const [globalProgress, setGlobalProgress] = useState(null);
  const [setWordsMap, setSetWordsMap] = useState({});
  const [progressLoading, setProgressLoading] = useState(false);

  const [error, setError] = useState('');
  const [isStarting, setIsStarting] = useState(false);

  // ── Load teams on mount ────────────────────────────────────
  useEffect(() => {
    if (!currentUser) { setTeamsLoading(false); return; }
    const load = async () => {
      try {
        const [created, joined] = await Promise.all([
          teamService.getTeamsByUser(currentUser.uid),
          teamService.getAllTeamsByUser(currentUser.uid),
        ]);
        const map = {};
        [...created, ...joined].forEach((t) => { map[t.id] = t; });
        const all = Object.values(map);
        setTeams(all);
        // Auto-select if teams exist
        if (all.length > 0) setSelectedTeam(all[0]);
      } catch (e) {
        console.error('Error loading teams:', e);
      }
      setTeamsLoading(false);
    };
    load();
  }, [currentUser]);

  // ── Load team progress (reloads when selected team changes) ──
  useEffect(() => {
    if (!selectedTeam) { setGlobalProgress(null); return; }
    const load = async () => {
      setProgressLoading(true);
      try {
        const prog = await trainingService.getProgress(selectedTeam.id);
        setGlobalProgress(prog);
        const results = await Promise.all(SETS.map((s) => wordService.getTrainingWords(s.limit)));
        const map = {};
        SETS.forEach((s, i) => { map[s.limit] = results[i]; });
        setSetWordsMap(map);
      } catch (e) {
        console.error('Error loading progress:', e);
      }
      setProgressLoading(false);
    };
    load();
  }, [selectedTeam]);


  // ── Subscribe to selected team real-time ───────────────────
  useEffect(() => {
    if (unsubTeamRef.current) { unsubTeamRef.current(); unsubTeamRef.current = null; }
    if (!selectedTeam) { setLiveTeam(null); return; }

    const myRole = teamService.getUserRole(selectedTeam, currentUser?.uid);
    teamService.joinTrainingSession(selectedTeam.id, currentUser.uid, myRole).catch(console.error);

    unsubTeamRef.current = teamService.subscribeToTeam(selectedTeam.id, (data) => {
      setLiveTeam(data);
    });

    return () => {
      if (unsubTeamRef.current) { unsubTeamRef.current(); unsubTeamRef.current = null; }
      if (currentUser && selectedTeam) {
        teamService.leaveTrainingSession(selectedTeam.id, currentUser.uid).catch(console.error);
      }
    };
  }, [selectedTeam, currentUser, navigate]);

  // ── Auto-redirect when gameState becomes active ───────────────
  useEffect(() => {
    if (!liveTeam || !currentUser) return;
    const gs = liveTeam?.trainingSession?.gameState;
    const activeStatuses = ['countdown', 'playing', 'paused', 'waiting_ready'];
    if (gs && activeStatuses.includes(gs.status)) {
      const myRole = teamService.getUserRole(liveTeam, currentUser.uid);
      navigate('/training/game', {
        state: { teamId: liveTeam.id, role: myRole, trainingMode: 'multiplayer' },
      });
    }
  }, [liveTeam, currentUser, navigate]);

  // ── Click outside to close team dropdown ───────────────────
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (teamDropdownRef.current && !teamDropdownRef.current.contains(event.target)) {
        setTeamDropdownOpen(false);
      }
    };

    if (teamDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [teamDropdownOpen]);

  // ── Handlers ───────────────────────────────────────────────
  const handleSelectTeam = (team) => {
    setSelectedTeam(team);
    setTeamDropdownOpen(false);
  };

  const handleSetChange = async (limit) => {
    if (!selectedTeam) return;
    try {
      await teamService.setTrainingWordSet(selectedTeam.id, limit);
    } catch (e) {
      console.error('Error updating word set:', e);
    }
  };

  const handleStart = async () => {
    if (!selectedTeam || isStarting) return;
    setIsStarting(true);
    setError('');
    const wordSet = liveTeam?.trainingSession?.wordSet || 100;
    try {
      // 1. Carica le parole
      const words = await wordService.getTrainingWords(wordSet);
      const wordList = words.map((w) => ({ id: w.id, text: w.text || w.word || '' }));

      // 2. Scrivi il gameState su Firestore — questo triggera il redirect per TUTTI
      await teamService.startTrainingGame(
        selectedTeam.id,
        wordList,
        currentUser.uid,
      );
      // navigate avverrà tramite il listener sopra
    } catch (e) {
      console.error('Errore avvio allenamento:', e);
      setError('Impossibile avviare la sessione. Riprova.');
      setIsStarting(false);
    }
  };

  const getSetInfo = (limit) => {
    if (!globalProgress || !setWordsMap[limit]) return { pct: 0 };
    return trainingService.getSetStats(globalProgress, setWordsMap[limit]);
  };

  // ── Derived: real-time present members by role ─────────────
  const trainingMembers = liveTeam?.trainingSession?.members || {}; // { uid: role }
  const currentWordSet = liveTeam?.trainingSession?.wordSet || 100;

  const roleSlots = ['speaker1', 'speaker2', 'guesser'].map((roleKey) => {
    const presentUid = Object.entries(trainingMembers).find(([, r]) => r === roleKey)?.[0];
    const memberInfo = liveTeam?.members?.find((m) => m.userId === presentUid);
    const isMe = presentUid === currentUser?.uid;
    return { roleKey, presentUid, memberName: memberInfo?.name, isMe };
  });

  const allPresent = roleSlots.every((s) => s.presentUid);
  const myRole = selectedTeam ? teamService.getUserRole(selectedTeam, currentUser?.uid) : null;

  // ── Render ─────────────────────────────────────────────────
  if (teamsLoading) {
    return (
      <div className="training-page">
        <TopBar title="Allenamento" onBack={() => navigate('/')} />
        <LoadingSpinner fullPage />
      </div>
    );
  }

  if (teams.length === 0) {
    return (
      <div className="training-page">
        <TopBar title="Allenamento" onBack={() => navigate('/')} />
        <div className="training-container training-empty-state">
          <span className="training-empty-icon">🏆</span>
          <p className="training-empty-text">Unisciti a una squadra per allenarti.</p>
          <button className="btn-premium" onClick={() => navigate('/teams')} id="btn-go-teams">
            Vai a Squadre →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="training-page">
      <TopBar
        title="Allenamento"
        onBack={() => navigate('/')}
        actions={
          <div className="training-team-dropdown-wrap" ref={teamDropdownRef}>
            <button
              className="training-team-pill"
              onClick={() => setTeamDropdownOpen((v) => !v)}
              id="btn-select-team"
            >
              🏆 {selectedTeam ? selectedTeam.name : 'Scegli la squadra'}
              <span className="training-team-pill__caret">{teamDropdownOpen ? '▲' : '▼'}</span>
            </button>
            {teamDropdownOpen && (
              <div className="training-team-dropdown">
                {teams.map((t) => {
                  const r = teamService.getUserRole(t, currentUser?.uid);
                  const rInfo = ROLE_COLORS[r];
                  return (
                    <button
                      key={t.id}
                      className={`training-team-dropdown__item ${selectedTeam?.id === t.id ? 'is-active' : ''}`}
                      onClick={() => handleSelectTeam(t)}
                    >
                      <span className="training-team-dropdown__name">{t.name}</span>
                      {rInfo && (
                        <span className="training-team-dropdown__role" style={{ color: rInfo.text }}>
                          {rInfo.icon} {rInfo.label}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        }
      />

      <div className="training-container">

        {!selectedTeam ? (
          <div className="training-placeholder">
            <span>👆</span>
            <p>Seleziona una squadra per iniziare la sessione di allenamento.</p>
          </div>
        ) : (
          <>
            {/* ── Set Selector ─────────────────────────── */}
            <section className="training-section">
              <h2 className="training-section__title">Set di parole</h2>
              <div className="training-sets-grid">
                {SETS.map((s) => {
                  const { pct } = getSetInfo(s.limit);
                  const isSelected = currentWordSet === s.limit;
                  return (
                    <button
                      key={s.limit}
                      className={`training-set-btn ${isSelected ? 'is-selected' : ''}`}
                      onClick={() => handleSetChange(s.limit)}
                      id={`btn-set-${s.limit}`}
                    >
                      <span className="training-set-btn__label">{s.label}</span>
                      <div className="training-set-btn__progress-row">
                        <div className="training-set-btn__progress-bar">
                          <div className="training-set-btn__bar" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="training-set-btn__pct">{progressLoading ? '…' : `${pct}%`}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* ── Role Slots ───────────────────────────── */}
            <section className="training-section">
              <h2 className="training-section__title">
                Presenti nella sessione
                <span className="training-section__count">
                  {roleSlots.filter((s) => s.presentUid).length}/3
                </span>
              </h2>
              <div className="training-role-slots">
                {roleSlots.map(({ roleKey, presentUid, memberName, isMe }) => {
                  const colors = ROLE_COLORS[roleKey];
                  const isPresent = !!presentUid;
                  const isMySlot = roleKey === myRole;
                  return (
                    <div
                      key={roleKey}
                      className={`training-role-slot ${isPresent ? 'is-present' : ''} ${isMySlot ? 'is-mine' : ''}`}
                      style={isPresent ? {
                        background: colors.bg,
                        borderColor: colors.border,
                      } : {}}
                    >
                      <span className="training-role-slot__icon">{colors.icon}</span>
                      <div className="training-role-slot__info">
                        <span className="training-role-slot__label" style={isPresent ? { color: colors.text } : {}}>
                          {colors.label}
                        </span>
                        <span className="training-role-slot__name">
                          {isPresent
                            ? `${memberName || 'Membro'}${isMe ? ' (tu)' : ''}`
                            : 'In attesa…'}
                        </span>
                      </div>
                      <div className={`training-role-slot__dot ${isPresent ? 'is-online' : ''}`}
                        style={isPresent ? { background: colors.border } : {}} />
                    </div>
                  );
                })}
              </div>

              {error && <p className="training-error">{error}</p>}

              <button
                className={`training-start-btn ${(!allPresent || isStarting) ? 'is-disabled' : ''}`}
                onClick={handleStart}
                disabled={!allPresent || isStarting}
                id="btn-start-training"
              >
                {isStarting
                  ? 'Caricamento…'
                  : allPresent
                    ? '▶ AVVIA ALLENAMENTO'
                    : `In attesa di ${3 - roleSlots.filter((s) => s.presentUid).length} membro/i…`}
              </button>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default Training;
